import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as Component from './Select.styles'

import useOutsideClickHandler from 'hooks/outsideClickHandler'
import DOMPurify from 'dompurify'
import { Tooltip } from '../index'

const Select = (props) => {
  const {
    id,
    className,
    value,
    placeholder,
    options,
    handleSelect,
    backgroundColor,
    context,
    excludeCurrentValue,
    disabled,
    ...other
  } = props

  const select = useRef(null)
  const [expand, setExpand] = useState(false)
  const [minWidth, setMinWidth] = useState(205)

  // https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  // calculate minWidth for SelectContainer
  const selectOptionsRef = useCallback((selectOptions) => {
    if (selectOptions !== null) {
      // make the node visible to calc the width
      selectOptions.style.display = 'block'
      setMinWidth(selectOptions.getBoundingClientRect().width)
      selectOptions.style.display = ''
    }
  }, [])

  useOutsideClickHandler(select, () => {
    setExpand(false)
  })

  const onHandleSelect = (newValue) => {
    setExpand(false)

    if (value !== newValue) handleSelect(newValue)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setExpand(false)
    }

    if (event.key === ' ') {
      setExpand(true)
    }
  }

  // props are not equal (here is refactoring potential)
  const getDisplayNameForValue = (value) => {
    const optionIndex = Object.keys(options).find(
      (index) => options[index].value === value
    )

    let valLabel
    if (options[optionIndex]?.label) {
      valLabel = options[optionIndex].label
    } else if (options[optionIndex]?.option) {
      valLabel = options[optionIndex].option
    }

    const hasValidToolTip =
      options[optionIndex]?.tooltip &&
      options[optionIndex]?.tooltip?.tip &&
      options[optionIndex]?.tooltip?.title

    return hasValidToolTip ? (
      <div className='active-value-with-tooltip'>
        <span
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(valLabel) }}
        />
        <Tooltip
          tooltipId={ `${ options[optionIndex].label }-tip-id` }
          infoIconId={ `${ options[optionIndex].label }-info-icon` }
          arrow='right'
          dialogTitle={ options[optionIndex]?.tooltip.title }>
          {options[optionIndex]?.tooltip.tip}
        </Tooltip>
      </div>
    ) : (
      <span
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(valLabel) }}
      />
    )
  }

  /**
   * get mapped select options
   * @param optionArray
   * @returns {*}
   */
  const getSelectOptions = (optionArray) =>
    optionArray.map((option, key) => {
      const optionLabel =
        props.context !== 'resultSort' ? option.option : option.label

      return (
        <Component.ListItem
          id={ `option-${ option.value }` }
          key={ key }
          role='treeitem'
          tabIndex='0'
          onClick={ () => onHandleSelect(option.value) }
          onKeyDown={ (event) =>
            event.key === 'Enter' && onHandleSelect(option.value)
          }
          aria-selected={ option.value === value }
          data-cy={ option.dataCy }
          title={ optionLabel }>
          {typeof option.option === 'object' ? (
            option.option
          ) : (
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(option.option),
              }}
            />
          )}
        </Component.ListItem>
      )
    })

  /**
   * get lable value for select
   * @returns {*}
   */
  const getActiveOptionLabel = () => {
    if (value === null || !getDisplayNameForValue(value)?.props) {
      return placeholder
    }
    return getDisplayNameForValue(value).props.children
  }

  return (
    <Component.SelectContainer
      minWidth={ minWidth }
      className={ className }
      role='combobox'
      aria-expanded={ expand }
      aria-label='select'
      onKeyDown={ (event) => handleKeyDown(event) }
      ref={ select }
      context={ context }
      { ...other }>
      <Component.Select
        id={ id }
        tabIndex='-1'
        onClick={ () => !disabled && setExpand(!expand) }>
        <Component.Selection
          tabIndex='0'
          role='textbox'
          aria-haspopup='true'
          aria-labelledby={ `${ id }-selection-title` }
          backgroundColor={ backgroundColor }
          disabled={ disabled }>
          <Component.SelectionTitle
            title={ getActiveOptionLabel() }
            id={ `${ id }-selection-title` }
            data-cy={ `${ id }-selection-title` }
            data-cy-selected-value={ value }>
            {value !== null ? (
              getDisplayNameForValue(value)
            ) : (
              <span>{placeholder}</span>
            )}
          </Component.SelectionTitle>
        </Component.Selection>
      </Component.Select>

      <Component.SelectOptions ref={ selectOptionsRef } expand={ expand }>
        <Component.List
          id={ `${ id }-select-list` }
          data-cy='radius-select-results'
          role='tree'
          aria-expanded='true'
          aria-hidden='false'>
          {getSelectOptions(
            excludeCurrentValue
              ? options.filter((option) => option.value !== value)
              : options
          )}
        </Component.List>
      </Component.SelectOptions>
    </Component.SelectContainer>
  )
}

Select.defaultProps = {
  backgroundColor: '#fff',
  excludeCurrentValue: false,
  disabled: false,
}

Select.propTypes = {
  /** Id of the select */
  id: PropTypes.string.isRequired,
  /** The CSS class name of the input wrapper element. */
  className: PropTypes.string,
  /** The select value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Placeholder */
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      option: PropTypes.node,
      tooltip: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
          tip: PropTypes.string,
          text: PropTypes.string,
        }),
      ]),
    })
  ),
  /** handle user selection */
  handleSelect: PropTypes.func.isRequired,
  /** Changes background color */
  backgroundColor: PropTypes.string,
  context: PropTypes.oneOf(['resultSort', 'default']),
  excludeCurrentValue: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default Select
